import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Archive as ArchiveIcon, Search, Award, FileText, History } from "lucide-react";
import { toast } from "sonner";

/**
 * الأرشيف — الوصول لبيانات أعوام قديمة (أبعد من نافذة السنتين المعروضة
 * في قائمة الأعوام أعلى الصفحة). لا يقرأ أو يعدِّل أي بيانات جديدة —
 * فقط يسهِّل الوصول لنفس شاشتي الشهادات ومراجعة الرصد الموجودتين
 * بالفعل، عبر ضبط السياق (المرحلة/العام) والانتقال إليهما مباشرة.
 */
export default function Archive() {
  const navigate = useNavigate();
  const { ctx, setContext, meta } = useApp();
  const stages = meta?.stages || [];

  return (
    <div className="space-y-6" data-testid="archive-page">
      <PageHeader
        icon={ArchiveIcon}
        overline="بيانات الأعوام السابقة"
        title="الأرشيف"
        subtitle="الوصول لشهادة طالب من أي عام سابق، أو مراجعة رصد صف كامل لعام قديم — بلا التقيّد بنافذة الأعوام المعروضة أعلى الصفحة."
      />

      <Tabs defaultValue="certificate" dir="rtl">
        <TabsList>
          <TabsTrigger value="certificate" data-testid="archive-tab-certificate">
            <Award className="h-4 w-4 me-1.5" /> بحث عن شهادة طالب
          </TabsTrigger>
          <TabsTrigger value="reports" data-testid="archive-tab-reports">
            <FileText className="h-4 w-4 me-1.5" /> مراجعة رصد لعام سابق
          </TabsTrigger>
        </TabsList>

        <TabsContent value="certificate">
          <CertificateSearchTab navigate={navigate} setContext={setContext} />
        </TabsContent>

        <TabsContent value="reports">
          <ReportsJumpTab navigate={navigate} setContext={setContext} stages={stages} currentStage={ctx.stage} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function CertificateSearchTab({ navigate, setContext }) {
  const [nationalId, setNationalId] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const search = async () => {
    const nid = nationalId.trim();
    if (!nid) return toast.error("اكتب الرقم القومي أولًا");
    setLoading(true);
    setResults(null);
    try {
      const { data } = await api.get("/students/search-by-national-id", { params: { national_id: nid } });
      setResults(data);
      if (data.length === 0) toast.error("لا يوجد طالب بهذا الرقم القومي في أي عام");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const openCertificate = (record) => {
    setContext({ stage: record.stage, year: record.year });
    toast.success(`تم ضبط السياق على ${record.stage} — ${record.year}. اختر الصف "${record.grade}" ثم الطالب.`);
    navigate("/certificates");
  };

  return (
    <div className="space-y-4 pt-4">
      <div className="rounded-xl border border-border bg-white p-4 flex items-end gap-3">
        <div className="flex-1 space-y-1.5">
          <Label className="text-[12px]">الرقم القومي للطالب</Label>
          <Input value={nationalId} onChange={(e) => setNationalId(e.target.value)}
                 placeholder="اكتب الرقم القومي كاملًا" className="h-10"
                 onKeyDown={(e) => e.key === "Enter" && search()}
                 data-testid="archive-national-id-input" />
        </div>
        <Button onClick={search} disabled={loading} className="h-10" data-testid="archive-search-btn">
          <Search className="h-4 w-4 me-1.5" /> {loading ? "جارٍ البحث…" : "بحث"}
        </Button>
      </div>

      {results && results.length > 0 && (
        <div className="rounded-xl border border-border bg-white overflow-hidden">
          <div className="px-4 py-2.5 border-b border-border bg-slate-50 text-sm font-semibold flex items-center gap-1.5">
            <History className="h-4 w-4 text-primary" /> وُجِد الطالب في {results.length} عام
          </div>
          <div className="divide-y divide-slate-100">
            {results.map((r) => (
              <div key={r.id} className="px-4 py-3 flex items-center justify-between gap-3">
                <div>
                  <div className="font-semibold text-sm">{r.name}</div>
                  <div className="text-[12px] text-muted-foreground mt-0.5">
                    {r.year} — {r.stage} — {r.grade} {r.class ? `— فصل ${r.class}` : ""}
                  </div>
                </div>
                <Button size="sm" variant="outline" onClick={() => openCertificate(r)} data-testid={`archive-open-cert-${r.id}`}>
                  <Award className="h-3.5 w-3.5 me-1" /> عرض الشهادة
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {results && results.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-10 text-center text-muted-foreground text-sm">
          لا يوجد طالب بهذا الرقم القومي
        </div>
      )}
    </div>
  );
}

function ReportsJumpTab({ navigate, setContext, stages, currentStage }) {
  const [stage, setStage] = useState(currentStage || "");
  const [years, setYears] = useState([]);
  const [year, setYear] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!stage) { setYears([]); return; }
    setLoading(true);
    api.get("/archive/years", { params: { stage } })
      .then(({ data }) => { setYears(data.years); setYear(""); })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [stage]);

  const openReports = () => {
    if (!stage || !year) return toast.error("اختر المرحلة والعام أولًا");
    setContext({ stage, year });
    navigate("/reports");
  };

  return (
    <div className="space-y-4 pt-4">
      <div className="rounded-xl border border-border bg-white p-4 grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
        <div className="space-y-1.5">
          <Label className="text-[12px]">المرحلة</Label>
          <Select value={stage} onValueChange={setStage}>
            <SelectTrigger className="h-10" data-testid="archive-reports-stage"><SelectValue placeholder="اختر المرحلة" /></SelectTrigger>
            <SelectContent>
              {stages.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-[12px]">العام (كل الأعوام التي بها بيانات فعليًا)</Label>
          <Select value={year} onValueChange={setYear} disabled={!stage || loading}>
            <SelectTrigger className="h-10" data-testid="archive-reports-year">
              <SelectValue placeholder={loading ? "جارٍ التحميل…" : "اختر العام"} />
            </SelectTrigger>
            <SelectContent>
              {years.length === 0 && !loading && (
                <div className="px-3 py-2 text-[12.5px] text-muted-foreground">لا توجد بيانات لهذه المرحلة</div>
              )}
              {years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={openReports} disabled={!stage || !year} className="h-10">
          <FileText className="h-4 w-4 me-1.5" /> عرض مراجعة الرصد
        </Button>
      </div>
      <p className="text-[12.5px] text-muted-foreground">
        بعد الانتقال، اختر الصف من شاشة "مراجعة الرصد" نفسها — العام سيكون مضبوطًا مسبقًا تلقائيًا.
      </p>
    </div>
  );
}
